import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import { currentBundle } from '@/lib/offline/bundle';
import type { IdMapping } from '@/lib/offline/db';
import { fmtInt } from '@/lib/format';
import { useNaves } from '@/lib/queries/naves';
import { useCamada } from '@/lib/queries/camadas';
import { useCrearLote, useEditarLote, useLotesDeCamada } from '@/lib/queries/lotesNave';
import { MobileFormPage } from '@/components/MobileFormPage';
import { useAuthStore } from '@/stores/auth';
import { extractApiMessage } from '@/lib/api';
import type { LoteNave } from '@/lib/types';

export function AsignarLotesPage() {
  const { camadaId: parameter } = useParams<{ camadaId: string }>();
  const ownerId = useAuthStore((state) => state.user?.id ?? null);
  const mappings = useEntityMappings();
  const farmId = currentBundle()?.explotacion_id ?? null;
  const identity = `${ownerId}:${parameter}`;
  const context = useRef({ identity, farmId, revision: 0 });
  if (context.current.identity !== identity) context.current = { identity, farmId, revision: 0 };
  else if (farmId != null) {
    if (context.current.farmId != null && context.current.farmId !== farmId)
      context.current.revision++;
    context.current.farmId = farmId;
  }
  return (
    <AssignmentForm
      key={`${identity}:${context.current.revision}`}
      parameter={parameter}
      ownerId={ownerId}
      farmId={farmId}
      mappings={mappings}
    />
  );
}

function AssignmentForm({
  parameter,
  ownerId,
  farmId,
  mappings,
}: {
  parameter: string | undefined;
  ownerId: number | null;
  farmId: number | null;
  mappings: IdMapping[];
}) {
  const camadaId = useEntityId(parameter, 'camadas');
  const user = useAuthStore((state) => state.user);
  const camadaQ = useCamada(camadaId),
    navesQ = useNaves(),
    lotesQ = useLotesDeCamada(camadaId);
  const crear = useCrearLote(camadaId ?? 0),
    editar = useEditarLote(camadaId ?? 0);
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [confirmed, setConfirmed] = useState<Record<number, LoteNave>>({});
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const mapId = useCallback(
    (entity: string, id: number) =>
      mappings.find((mapping) => mapping.entity === entity && mapping.localId === id)?.remoteId ??
      id,
    [mappings],
  );
  const acknowledged = useCallback(
    (saved: LoteNave) =>
      (lotesQ.data ?? []).some(
        (row) =>
          mapId('lotes', row.id) === mapId('lotes', saved.id) &&
          (row.num_animales_asignados === saved.num_animales_asignados ||
            (row.updated_at &&
              (!saved.updated_at || Date.parse(row.updated_at) > Date.parse(saved.updated_at)))),
      ),
    [lotesQ.data, mapId],
  );
  // Keep successful rows only until the query observes their write or a newer version.
  useEffect(() => {
    setConfirmed((previous) => {
      const remaining = Object.fromEntries(
        Object.entries(previous).filter(([, row]) => !acknowledged(row)),
      );
      return Object.keys(remaining).length === Object.keys(previous).length ? previous : remaining;
    });
  }, [acknowledged]);
  const mappedEdits = Object.fromEntries(
    Object.entries(edits).map(([id, value]) => [mapId('naves', Number(id)), value]),
  );
  const baseline: Record<number, LoteNave> = {
    ...Object.fromEntries(
      (lotesQ.data ?? []).map((lote) => [
        mapId('naves', lote.nave_id),
        { ...lote, id: mapId('lotes', lote.id) },
      ]),
    ),
    ...Object.fromEntries(
      Object.values(confirmed)
        .filter((saved) => !acknowledged(saved))
        .map((saved) => [
          mapId('naves', saved.nave_id),
          { ...saved, id: mapId('lotes', saved.id) },
        ]),
    ),
  };
  const ready =
    camadaId != null &&
    camadaQ.isSuccess &&
    navesQ.isSuccess &&
    lotesQ.isSuccess &&
    !camadaQ.isError &&
    !navesQ.isError &&
    !lotesQ.isError &&
    camadaQ.data != null &&
    Array.isArray(navesQ.data) &&
    Array.isArray(lotesQ.data) &&
    Number.isSafeInteger(camadaQ.data.num_animales_total);
  const canEdit = user?.rol === 'admin' || user?.rol === 'ganadero';
  const busy = crear.isPending || editar.isPending;
  const naves = navesQ.data ?? [];
  const valueFor = (id: number) =>
    mappedEdits[mapId('naves', id)] ??
    (baseline[mapId('naves', id)]
      ? String(baseline[mapId('naves', id)].num_animales_asignados)
      : '');
  const changed = naves.filter((nave) => {
    const id = mapId('naves', nave.id),
      raw = mappedEdits[id];
    return (
      raw !== undefined &&
      (baseline[id]
        ? Number(raw) !== baseline[id].num_animales_asignados || raw.trim() === ''
        : Number(raw) !== 0)
    );
  });
  const dirty = changed.length > 0;
  const assigned = naves.reduce((total, nave) => {
    const value = Number(valueFor(nave.id));
    return total + (Number.isSafeInteger(value) && value > 0 ? value : 0);
  }, 0);
  const received = ready ? camadaQ.data!.num_animales_total : null;
  const difference = received == null ? null : received - assigned;
  const loadError = camadaQ.error ?? navesQ.error ?? lotesQ.error;

  async function onSave(): Promise<string | undefined> {
    let guardedFarm = farmId;
    const assertContext = () => {
      const actualFarm = currentBundle()?.explotacion_id ?? null;
      if (
        !mounted.current ||
        useAuthStore.getState().user?.id !== ownerId ||
        (guardedFarm != null && actualFarm != null && actualFarm !== guardedFarm)
      )
        throw new Error('La sesión o explotación ha cambiado. Revisa la asignación actual.');
      guardedFarm ??= actualFarm;
    };
    assertContext();
    if (!ready || !canEdit || camadaQ.data!.cerrada)
      throw new Error('No se puede guardar esta asignación.');
    const changes = changed.map((nave) => {
      const raw = valueFor(nave.id).trim(),
        quantity = Number(raw),
        id = mapId('naves', nave.id);
      if (
        !/^\d+$/.test(raw) ||
        !Number.isSafeInteger(quantity) ||
        quantity < 0 ||
        quantity > 2147483647 ||
        (baseline[id] && quantity === 0)
      )
        throw new Error(
          `Introduce un número entero positivo para ${nave.nombre || nave.codigo}. Una asignación existente no se elimina poniendo cero.`,
        );
      return { nave, id, quantity, existing: baseline[id] };
    });
    if (!changes.length) return undefined;
    for (const { id, quantity, existing } of changes) {
      assertContext();
      const saved = existing
        ? await editar.mutateAsync({ id: existing.id, num_animales_asignados: quantity })
        : await crear.mutateAsync({ nave_id: id, num_animales_asignados: quantity });
      assertContext();
      setConfirmed((previous) => ({ ...previous, [id]: saved }));
      setEdits((previous) => {
        const next = { ...previous };
        for (const key of Object.keys(next))
          if (mapId('naves', Number(key)) === id) delete next[Number(key)];
        return next;
      });
    }
    return `/camadas?camada=${camadaId}`;
  }
  return (
    <MobileFormPage
      title="Asignar naves"
      backTo={camadaId == null ? '/camadas' : `/camadas?camada=${camadaId}`}
      saveLabel="Guardar asignación"
      dirty={dirty}
      disabled={!dirty || !ready || !canEdit || camadaQ.data?.cerrada}
      onSave={onSave}
    >
      {loadError ? (
        <p role="alert" className="cm-form-note">
          {extractApiMessage(loadError, 'No se pudo cargar la asignación.')}
        </p>
      ) : !ready ? (
        <p className="cm-form-note">Cargando asignación…</p>
      ) : (
        <>
          <div className="cm-form-context">
            <strong>{camadaQ.data!.codigo_lote}</strong>
            <p>Prepara la distribución física. Los animales asignados permanecen en su nave.</p>
          </div>
          {!canEdit && (
            <p className="cm-form-note">Tu perfil permite consultar las asignaciones.</p>
          )}
          {camadaQ.data!.cerrada && <p className="cm-form-note">La camada está cerrada.</p>}
          <div className="cm-form-balance" aria-live="polite">
            <span>
              Recibidos <strong>{fmtInt(received!)}</strong>
            </span>
            <span>
              Asignados <strong>{fmtInt(assigned)}</strong>
            </span>
          </div>
          <p className="cm-form-note">
            {received === 0
              ? 'Preparación sin entradas. Asignar no registra animales recibidos.'
              : difference! > 0
                ? `${fmtInt(difference!)} ${difference === 1 ? 'animal sin asignar' : 'animales sin asignar'}.`
                : difference! < 0
                  ? `${fmtInt(-difference!)} ${difference === -1 ? 'animal asignado' : 'animales asignados'} por encima de los recibidos.`
                  : 'Recibidos y asignados coinciden.'}
          </p>
          {naves.length === 0 && (
            <p className="cm-form-note">
              No hay naves disponibles. <Link to="/ajustes/naves">Crear una nave</Link> para
              preparar la asignación.
            </p>
          )}
          {naves.map((nave) => (
            <div className="cm-form-field" key={nave.id}>
              <label htmlFor={`nave-${nave.id}`}>Animales en {nave.nombre || nave.codigo}</label>
              {nave.nombre && nave.nombre !== nave.codigo && (
                <span className="cm-form-note">{nave.codigo}</span>
              )}
              <input
                id={`nave-${nave.id}`}
                className="ui-field"
                type="number"
                inputMode="numeric"
                min={baseline[mapId('naves', nave.id)] ? 1 : 0}
                max={2147483647}
                step={1}
                placeholder="0"
                value={valueFor(nave.id)}
                disabled={!canEdit || camadaQ.data!.cerrada || busy}
                onChange={(event) =>
                  setEdits((previous) => {
                    const id = mapId('naves', nave.id);
                    const next = Object.fromEntries(
                      Object.entries(previous).filter(
                        ([key]) => mapId('naves', Number(key)) !== id,
                      ),
                    );
                    return { ...next, [id]: event.target.value };
                  })
                }
              />
              <p className="cm-form-note">
                {nave.capacidad_animales == null
                  ? 'Capacidad sin definir.'
                  : `Capacidad orientativa: ${fmtInt(nave.capacidad_animales)} animales.`}
                {baseline[mapId('naves', nave.id)] && ' Asignación existente; conserva su nave.'}
              </p>
            </div>
          ))}
        </>
      )}
    </MobileFormPage>
  );
}
export default AsignarLotesPage;
